import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAdminApiCallMetrics, listAdminOrganizations } from '../../admin/adminApi'
import type { AdminApiCallMetricsResponse, AdminOrganizationSummary } from '../../admin/types'
import { Button, Card, Field, I, Pill, gridColumns } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { DataPill, EmptyState, ErrorState, LoadingState } from './adminPageHelpers'
import { formatDate, numberLabel, percentLabel, tableContainerStyle, tdStyle, thStyle } from './adminPageFormatters'

interface AdminApiCallsPageProps {
  accessToken: string
  onOpenOrganization: (organizationId: number) => void
  onOpenProxy: (organizationId: number) => void
  onOpenStockDiagnostics?: () => void
}

interface ApiCallFilters {
  organizationId: string
  provider: string
  from: string
  to: string
}

const EMPTY_FILTERS: ApiCallFilters = { organizationId: '', provider: '', from: '', to: '' }
const RETAINED_DAYS = 90

function exceedsRetainedRange(filters: ApiCallFilters): boolean {
  if (!filters.from && !filters.to) return false
  const today = new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`)
  const oldestRetained = new Date(today)
  oldestRetained.setUTCDate(oldestRetained.getUTCDate() - (RETAINED_DAYS - 1))
  const effectiveTo = filters.to || today.toISOString().slice(0, 10)
  const toTime = Date.parse(`${effectiveTo}T00:00:00Z`)
  const defaultFrom = new Date(toTime)
  defaultFrom.setUTCDate(defaultFrom.getUTCDate() - 6)
  const fromTime = filters.from ? Date.parse(`${filters.from}T00:00:00Z`) : defaultFrom.getTime()
  const days = Math.round((toTime - fromTime) / 86_400_000)
  return fromTime < oldestRetained.getTime() || toTime > today.getTime() || days < 0 || days >= RETAINED_DAYS
}

export function AdminApiCallsPage({ accessToken, onOpenOrganization, onOpenProxy, onOpenStockDiagnostics }: AdminApiCallsPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [organizations, setOrganizations] = useState<AdminOrganizationSummary[]>([])
  const [metrics, setMetrics] = useState<AdminApiCallMetricsResponse | null>(null)
  const [draftFilters, setDraftFilters] = useState<ApiCallFilters>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<ApiCallFilters>(EMPTY_FILTERS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [filterError, setFilterError] = useState(false)

  const load = useCallback(function load() {
    setLoading(true)
    setError(false)
    Promise.all([
      listAdminOrganizations(accessToken),
      getAdminApiCallMetrics(accessToken, {
        organizationId: appliedFilters.organizationId ? Number(appliedFilters.organizationId) : null,
        provider: appliedFilters.provider || null,
        from: appliedFilters.from || null,
        to: appliedFilters.to || null,
      }),
    ])
      .then(([orgs, response]) => {
        setOrganizations(orgs)
        setMetrics(response)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [accessToken, appliedFilters])

  useEffect(() => {
    void Promise.resolve().then(load)
  }, [load])

  const providers = useMemo(() => {
    const values = new Set<string>()
    metrics?.calls.forEach(row => values.add(row.provider))
    metrics?.quotas.forEach(row => values.add(row.provider))
    metrics?.health.forEach(row => values.add(row.provider))
    metrics?.operations.forEach(row => values.add(row.provider))
    return Array.from(values).sort()
  }, [metrics])

  function applyFilters() {
    if (exceedsRetainedRange(draftFilters)) {
      setFilterError(true)
      return
    }
    setFilterError(false)
    setAppliedFilters({ ...draftFilters })
  }

  if (loading) return <LoadingState label={t('admin.apiCalls.loading')} />
  if (error || !metrics) return <ErrorState title={t('admin.apiCalls.error.title')} body={t('admin.apiCalls.error.body')} onRetry={load} />

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {onOpenStockDiagnostics && <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Button kind="secondary" icon={I.alert} onClick={onOpenStockDiagnostics}>{t('admin.apiCalls.actions.stockDiagnostics')}</Button></div>}
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(5, minmax(0, 1fr)) auto'), gap: 12, alignItems: 'end' }}>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{t('admin.apiCalls.filters.organization')}</div>
            <select value={draftFilters.organizationId} onChange={event => setDraftFilters(current => ({ ...current, organizationId: event.target.value }))} style={selectStyle}>
              <option value="">{t('admin.common.allOrganizations')}</option>
              {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
            </select>
          </label>
          <Field label={t('admin.apiCalls.filters.provider')} placeholder={t('admin.apiCalls.filters.providerPlaceholder')} value={draftFilters.provider} onChange={event => setDraftFilters(current => ({ ...current, provider: event.target.value.toUpperCase() }))} />
          <Field label={t('admin.apiCalls.filters.from')} type="date" value={draftFilters.from} onChange={event => setDraftFilters(current => ({ ...current, from: event.target.value }))} />
          <Field label={t('admin.apiCalls.filters.to')} type="date" value={draftFilters.to} onChange={event => setDraftFilters(current => ({ ...current, to: event.target.value }))} />
          <Button kind="secondary" icon={I.filter} onClick={applyFilters}>{t('admin.apiCalls.filters.apply')}</Button>
        </div>
        <div role={filterError ? 'alert' : undefined} style={{ color: filterError ? 'var(--danger)' : 'var(--fg-subtle)', fontSize: 12, marginTop: 10 }}>
          {filterError ? t('admin.apiCalls.filters.rangeError') : t('admin.apiCalls.filters.retentionHint')}
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(5, minmax(0, 1fr))'), gap: 12 }}>
        <Summary label={t('admin.apiCalls.summary.auditedAttempts')} value={numberLabel(metrics.summary.auditedAttempts, i18n.language)} />
        <Summary label={t('admin.apiCalls.summary.successes')} value={numberLabel(metrics.summary.successfulResponses, i18n.language)} />
        <Summary label={t('admin.apiCalls.summary.failures')} value={numberLabel(metrics.summary.failedAttempts, i18n.language)} />
        <Summary label={t('admin.apiCalls.summary.localRejections')} value={numberLabel(metrics.summary.knownLocalRejections, i18n.language)} />
        <Summary label={t('admin.apiCalls.summary.quotaUsage')} value={numberLabel(metrics.summary.quotaCallsUsed, i18n.language)} />
      </div>

      <Card>
        <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.apiCalls.trend.title')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(metrics.trend.length, 1)}, minmax(44px, 1fr))`, gap: 10, marginTop: 18, overflowX: 'auto', paddingBottom: 4 }}>
          {metrics.trend.map(day => {
            const maximum = Math.max(...metrics.trend.map(item => item.auditedAttempts), 1)
            const height = Math.max(day.auditedAttempts === 0 ? 2 : 12, Math.round((day.auditedAttempts / maximum) * 112))
            return (
              <div key={day.day} style={{ display: 'grid', gap: 6, alignItems: 'end', minWidth: 44 }} aria-label={t('admin.apiCalls.trend.dayLabel', { day: formatDate(day.day, i18n.language, day.day), attempts: day.auditedAttempts })}>
                <div style={{ color: 'var(--fg-muted)', fontSize: 12, textAlign: 'center' }}>{numberLabel(day.auditedAttempts, i18n.language)}</div>
                <div style={{ height: 116, display: 'flex', alignItems: 'end', justifyContent: 'center', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ width: '70%', height, borderRadius: 'var(--r-sm) var(--r-sm) 0 0', background: 'var(--brand-500)' }} />
                </div>
                <div style={{ color: 'var(--fg-subtle)', fontSize: 11, textAlign: 'center', whiteSpace: 'nowrap' }}>{formatDate(day.day, i18n.language, day.day)}</div>
              </div>
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', color: 'var(--fg-muted)', fontSize: 12, marginTop: 12 }}>
          <span>{t('admin.apiCalls.trend.successes', { count: metrics.summary.successfulResponses })}</span>
          <span>{t('admin.apiCalls.trend.failures', { count: metrics.summary.failedAttempts })}</span>
          <span>{t('admin.apiCalls.trend.localRejections', { count: metrics.summary.knownLocalRejections })}</span>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) minmax(0, 1fr)'), gap: 18 }}>
        <Card>
          <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.apiCalls.quotaHealth.title')}</h2>
          <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
            {metrics.quotaHealth.length === 0 ? <EmptyState title={t('admin.apiCalls.quotaHealth.emptyTitle')} body={t('admin.apiCalls.quotaHealth.emptyBody')} /> : metrics.quotaHealth.map(row => (
              <div key={`${row.organizationId}-${row.provider}`} style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}><strong>{row.organizationName}</strong><Pill tone="info" size="sm">{row.provider}</Pill></div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--fg-subtle)', fontSize: 12 }}>{t('admin.apiCalls.quotaHealth.quotaLabel')} <DataPill value={row.quotaStatus} /></span>
                  {row.providerHealthStatus && <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--fg-subtle)', fontSize: 12 }}>{t('admin.apiCalls.quotaHealth.providerLabel')} <DataPill value={row.providerHealthStatus} /></span>}
                </div>
                <div style={{ color: 'var(--fg-muted)', marginTop: 7 }}>
                  {row.callsUsed == null || row.callLimit == null || row.usageRatio == null
                    ? t('admin.apiCalls.quotaHealth.notRecorded')
                    : t('admin.apiCalls.quotaHealth.usage', { used: numberLabel(row.callsUsed, i18n.language), limit: numberLabel(row.callLimit, i18n.language), percent: percentLabel(row.usageRatio, i18n.language) })}
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.apiCalls.attribution.title')}</h2>
          <div style={{ color: 'var(--fg-muted)', marginTop: 12 }}>{t('admin.apiCalls.attribution.coverage', { percent: percentLabel(metrics.summary.orderAttributionCoverage, i18n.language) })}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 14 }}>
            <Summary label={t('admin.apiCalls.attribution.linkedAttempts')} value={numberLabel(metrics.summary.attributedAttempts, i18n.language)} />
            <Summary label={t('admin.apiCalls.attribution.distinctOrders')} value={numberLabel(metrics.summary.distinctParentLinkedOrders, i18n.language)} />
          </div>
          <div style={{ color: 'var(--fg-subtle)', fontSize: 12, marginTop: 14 }}>{t('admin.apiCalls.attribution.limitation')}</div>
        </Card>
      </div>

      <Card>
        <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.apiCalls.operations.title')}</h2>
        {metrics.operations.length === 0 ? <div style={{ marginTop: 12 }}><EmptyState title={t('admin.apiCalls.operations.emptyTitle')} body={t('admin.apiCalls.operations.emptyBody')} /></div> : (
          <div style={{ ...tableContainerStyle, marginTop: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr>
              <th style={thStyle}>{t('admin.apiCalls.calls.table.provider')}</th><th style={thStyle}>{t('admin.apiCalls.calls.table.endpoint')}</th><th style={thStyle}>{t('admin.apiCalls.summary.auditedAttempts')}</th><th style={thStyle}>{t('admin.apiCalls.summary.successes')}</th><th style={thStyle}>{t('admin.apiCalls.summary.failures')}</th><th style={thStyle}>{t('admin.apiCalls.summary.localRejections')}</th><th style={thStyle}>{t('admin.apiCalls.operations.orderCoverage')}</th>
            </tr></thead><tbody>{metrics.operations.map(row => <tr key={`${row.provider}-${row.httpMethod}-${row.endpoint}`}>
              <td style={tdStyle}><Pill tone="info" size="sm">{row.provider}</Pill></td><td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{row.httpMethod} {row.endpoint}</td><td style={tdStyle}>{numberLabel(row.auditedAttempts, i18n.language)}</td><td style={tdStyle}>{numberLabel(row.successfulResponses, i18n.language)}</td><td style={tdStyle}>{numberLabel(row.failedAttempts, i18n.language)}</td><td style={tdStyle}>{numberLabel(row.knownLocalRejections, i18n.language)}</td><td style={tdStyle}>{percentLabel(row.orderAttributionCoverage, i18n.language)}</td>
            </tr>)}</tbody></table>
          </div>
        )}
      </Card>

      <Card>
        <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.apiCalls.calls.title')}</h2>
        {metrics.calls.length === 0 ? <div style={{ marginTop: 12 }}><EmptyState title={t('admin.apiCalls.calls.emptyTitle')} body={t('admin.apiCalls.calls.emptyBody')} /></div> : (
          <div style={{ ...tableContainerStyle, marginTop: 14 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr>
            <th style={thStyle}>{t('admin.apiCalls.calls.table.day')}</th><th style={thStyle}>{t('admin.apiCalls.calls.table.organization')}</th><th style={thStyle}>{t('admin.apiCalls.calls.table.provider')}</th><th style={thStyle}>{t('admin.apiCalls.calls.table.endpoint')}</th><th style={thStyle}>{t('admin.apiCalls.calls.table.status')}</th><th style={thStyle}>{t('admin.apiCalls.calls.table.count')}</th><th style={thStyle}>{t('admin.apiCalls.calls.table.failures')}</th><th style={thStyle}>{t('admin.common.actions')}</th>
          </tr></thead><tbody>{metrics.calls.map((row, index) => <tr key={`${row.day}-${row.organizationId}-${row.provider}-${row.endpoint}-${index}`}>
            <td style={tdStyle}>{formatDate(row.day, i18n.language, t('admin.common.none'))}</td><td style={{ ...tdStyle, color: 'var(--fg)', fontWeight: 800 }}>{row.organizationName}</td><td style={tdStyle}><Pill tone="info" size="sm">{row.provider}</Pill></td><td style={{ ...tdStyle, maxWidth: 260, overflowWrap: 'anywhere' }}><span style={{ fontFamily: 'var(--font-mono)' }}>{row.httpMethod} {row.endpoint}</span></td><td style={tdStyle}><DataPill value={row.statusBucket} />{row.errorCode && <div style={{ marginTop: 5, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{row.errorCode}</div>}</td><td style={tdStyle}>{numberLabel(row.callCount, i18n.language)}</td><td style={tdStyle}>{numberLabel(row.failureCount, i18n.language)}</td><td style={tdStyle}><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}><Button kind="ghost" size="sm" onClick={() => onOpenOrganization(row.organizationId)}>{t('admin.apiCalls.actions.organization')}</Button><Button kind="ghost" size="sm" onClick={() => onOpenProxy(row.organizationId)}>{t('admin.apiCalls.actions.proxy')}</Button>{onOpenStockDiagnostics && row.endpoint.includes('/stock/v1/stock-update') && <Button kind="ghost" size="sm" onClick={onOpenStockDiagnostics}>{t('admin.apiCalls.actions.diagnostics')}</Button>}</div></td>
          </tr>)}</tbody></table></div>
        )}
      </Card>
      {providers.length > 0 && <div style={{ color: 'var(--fg-subtle)', fontSize: 12 }}>{t('admin.apiCalls.providerHint', { providers: providers.join(', ') })}</div>}
    </div>
  )
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <Card padding={18}>
      <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ marginTop: 8, color: 'var(--brand-900)', fontWeight: 900, fontSize: 26 }}>{value}</div>
    </Card>
  )
}

const selectStyle = {
  width: '100%',
  padding: '11px 14px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: 'var(--fg)',
  background: 'white',
  border: '1px solid var(--border-default)',
  borderRadius: 10,
  outline: 'none',
} as const
